import React, { useEffect, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import {
  CUSTOMER_ORDER_STATUS,
  CustomerOrderRow,
  customerOrderSourceLabel,
  customerOrderStatusLabel,
  getCustomerOrder,
  updateCustomerOrderStatus,
} from '../../services/customerOrders.service';
import { BottomSheet } from '../../components/list';
import { showApiError } from '../../services/apiError';

const TEXT = '#222525';
const MUTED = '#4A5353';
const TEAL = '#0E5566';
const DANGER = '#B3261E';

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-center justify-between" style={{ padding: '10px 0', borderBottom: '1px solid #EBF0F0' }}>
    <span className="font-inter" style={{ fontSize: '13px', color: MUTED }}>{label}</span>
    <span className="font-inter" style={{ fontSize: '14px', fontWeight: 500, color: TEXT, textAlign: 'right' }}>{value}</span>
  </div>
);

const CustomerOrderDetail = () => {
  const [language, setLanguage] = useState<any>(null);
  const [order, setOrder] = useState<CustomerOrderRow | null>(null);
  const [confirming, setConfirming] = useState<null | 'complete' | 'cancel'>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);
  const id = window.location.pathname.split('/').pop() || '';

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const t = (key: string) => (language && language?.[key] ? language?.[key] : key);

  const load = async () => {
    try {
      const response: any = await getCustomerOrder(id);
      setOrder(response?.data?.data ?? null);
    } catch (error) {
      console.error('Error loading customer order:', error);
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  const applyStatus = async () => {
    if (!order || !confirming) return;
    setBusy(true);
    try {
      await updateCustomerOrderStatus(order.id, {
        status:
          confirming === 'complete'
            ? CUSTOMER_ORDER_STATUS.COMPLETED
            : CUSTOMER_ORDER_STATUS.CANCELLED,
        cancelReason:
          confirming === 'cancel' && cancelReason.trim()
            ? cancelReason.trim()
            : undefined,
      });
      setConfirming(null);
      setCancelReason('');
      await load();
    } catch (error) {
      console.error('Error updating order status:', error);
      showApiError(error, { language });
    } finally {
      setBusy(false);
    }
  };

  if (!order) {
    return (
      <div className="h-full flex items-center justify-center font-inter" style={{ color: MUTED }}>
        {t('Loading..')}
      </div>
    );
  }

  const pending = order.status === CUSTOMER_ORDER_STATUS.PENDING;
  const money = (value: string | null | undefined) => `€ ${Number(value ?? 0).toFixed(2)}`;

  return (
    <div className="h-full relative" style={{ overflowY: 'auto', paddingBottom: '96px' }}>
      <div className="flex items-center" style={{ gap: '12px' }}>
        <button className="font-inter" style={{ color: TEAL, fontSize: '14px' }} onClick={() => webNavigate('/customerOrders')}>
          ← {t('Customer Orders')}
        </button>
      </div>

      <div className="lg:flex items-start justify-between" style={{ marginTop: '12px' }}>
        <div>
          <p className="font-inter" style={{ fontSize: '24px', fontWeight: 500, color: TEXT }}>
            {order.reference}
            {order.status === CUSTOMER_ORDER_STATUS.CANCELLED && (
              // Display-only suffix, derived from the status — never stored.
              <span style={{ color: DANGER }}>-C</span>
            )}
          </p>
          <p className="font-inter" style={{ fontSize: '13px', color: MUTED }}>
            {order.displayRef ? `${t('POS ref')}: ${order.displayRef} · ` : ''}
            {t(customerOrderSourceLabel(order.source))} · {t(customerOrderStatusLabel(order.status))}
          </p>
        </div>
        {pending && (
          <div className="flex" style={{ gap: '8px', marginTop: '8px' }}>
            <button
              className="font-inter"
              style={{ background: TEAL, color: '#FFF', borderRadius: '10px', padding: '10px 16px', fontSize: '14px', fontWeight: 500 }}
              onClick={() => setConfirming('complete')}
            >
              {t('Mark Completed')}
            </button>
            <button
              className="font-inter"
              style={{ background: '#FFF', color: DANGER, border: `1px solid ${DANGER}`, borderRadius: '10px', padding: '10px 16px', fontSize: '14px', fontWeight: 500 }}
              onClick={() => setConfirming('cancel')}
            >
              {t('Cancel Order')}
            </button>
          </div>
        )}
      </div>

      <div className="lg:grid lg:grid-cols-2" style={{ gap: '20px', marginTop: '20px' }}>
        <div style={{ padding: '16px', borderRadius: '12px', border: '1px solid #EBF0F0' }}>
          <p className="font-inter" style={{ fontSize: '15px', fontWeight: 600, color: TEXT, marginBottom: '6px' }}>
            {t('Customer')}
          </p>
          <Row label={t('Customer Name')} value={order.customerName} />
          <Row label={t('Phone')} value={order.customerPhone ?? '-'} />
          <Row label={t('Requested For')} value={order.requestedFor ? new Date(order.requestedFor).toLocaleString() : t('ASAP')} />
          <Row label={t('Placed At')} value={new Date(order.placedAt).toLocaleString()} />
          {order.notes && <Row label={t('Notes')} value={order.notes} />}
          {order.cancelReason && <Row label={t('Cancellation Reason')} value={order.cancelReason} />}
          {!!order.allergens?.length && (
            <Row
              label={t('Allergy declarations')}
              value={order.allergens.map(a => a.allergenName).join(', ')}
            />
          )}
        </div>

        <div style={{ padding: '16px', borderRadius: '12px', border: '1px solid #EBF0F0', marginTop: '16px' }} className="lg:!mt-0">
          <p className="font-inter" style={{ fontSize: '15px', fontWeight: 600, color: TEXT, marginBottom: '6px' }}>
            {t('Order lines')}
          </p>
          {(order.lines ?? []).map(line => (
            <div key={line.id} className="flex items-center justify-between" style={{ padding: '10px 0', borderBottom: '1px solid #EBF0F0', paddingLeft: line.parentDetailId ? '16px' : 0 }}>
              <span className="font-inter" style={{ fontSize: '14px', color: TEXT }}>
                {Number(line.quantity)} × {line.productName}
                {line.productSku ? <span style={{ color: MUTED, fontSize: '12px' }}> · {line.productSku}</span> : null}
              </span>
              <span className="font-inter" style={{ fontSize: '14px', fontWeight: 500, color: TEXT }}>{money(line.amount)}</span>
            </div>
          ))}
          <Row label={t('Net')} value={money(order.net)} />
          <Row label={t('Tax')} value={money(order.tax)} />
          <Row label={t('Order Total')} value={<span style={{ fontSize: '16px', fontWeight: 600 }}>{money(order.amount)}</span>} />
        </div>
      </div>

      <BottomSheet
        open={confirming !== null}
        title={confirming === 'cancel' ? t('Cancel Order') : t('Mark Completed')}
        onClose={() => { setConfirming(null); setCancelReason(''); }}
      >
        <div style={{ padding: '4px 0 8px' }}>
          <p className="font-inter" style={{ fontSize: '14px', color: MUTED }}>
            {confirming === 'cancel' ? t('Cancel this order for') : t('Complete this order for')}{' '}
            <b style={{ color: TEXT }}>{order.customerName}</b> — <b style={{ color: TEXT }}>{order.reference}</b>?
          </p>
          {confirming === 'cancel' && (
            <textarea
              className="font-inter"
              style={{ width: '100%', minHeight: '72px', marginTop: '12px', padding: '12px', borderRadius: '10px', border: '1px solid #D5DEDE', fontSize: '14px' }}
              placeholder={t('Reason (optional)')}
              value={cancelReason}
              onChange={event => setCancelReason(event.target.value)}
            />
          )}
          <div className="flex" style={{ gap: '8px', marginTop: '16px' }}>
            <button
              className="font-inter flex-1"
              style={{ background: confirming === 'cancel' ? DANGER : TEAL, color: '#FFF', borderRadius: '10px', padding: '12px', fontSize: '14px', fontWeight: 500, opacity: busy ? 0.6 : 1 }}
              disabled={busy}
              onClick={applyStatus}
            >
              {confirming === 'cancel' ? t('Cancel Order') : t('Mark Completed')}
            </button>
            <button
              className="font-inter flex-1"
              style={{ background: '#FFF', color: TEXT, border: '1px solid #D5DEDE', borderRadius: '10px', padding: '12px', fontSize: '14px', fontWeight: 500 }}
              onClick={() => { setConfirming(null); setCancelReason(''); }}
            >
              {t('Keep Order')}
            </button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
};

export default CustomerOrderDetail;
